import { Resources } from 'constants/resources';

import { AxiosError, AxiosResponse } from 'axios';
import { axios } from 'config/axios';
import qs from 'qs';
import { Credentials } from 'types/embedded/credentials';
import { MerchantAccess, MerchantCredentials } from 'types/embedded/merchant';
import {
  MerchantNotification,
  MerchantNotificationTransport,
} from 'types/embedded/merchantNotification';
import { MerchantPayoutControl } from 'types/embedded/merchantPayoutControl';
import { getId } from 'utils';

export type GetMerchantNotificationsResponse = {
  merchantNotifications: MerchantNotification[];
  totalItems: number;
};

export const getMerchantPayoutControls = async (id: string): Promise<MerchantPayoutControl> => {
  return axios
    .get(`${Resources.Merchants}/${id}/merchant_payout_controls`)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.message));
};

export const deleteMerchantPayoutControls = async (id: number): Promise<AxiosResponse> => {
  return axios
    .delete(`merchant_payout_controls/${id}`)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.message));
};

export const createMerchantPayoutControls = async (
  merchant: string,
): Promise<AxiosResponse<MerchantPayoutControl>> => {
  const response = await axios.post('merchant_payout_controls', { merchant });

  return response.data;
};

export const merchantPayoutControlsUpdateActivity = async (
  active: boolean,
  id: number,
): Promise<AxiosResponse<{ active: boolean }>> => {
  return axios
    .patch(
      `merchant_payout_controls/${id}/active`,
      { active },
      {
        headers: {
          'content-type': 'application/merge-patch+json',
        },
      },
    )
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

type MerchantPayoutControlsUpdateMappingType = {
  payoutControlId: number;
  mappingId?: string | null;
};

export const merchantPayoutControlsUpdateMapping = async ({
  payoutControlId,
  mappingId = null,
}: MerchantPayoutControlsUpdateMappingType): Promise<AxiosResponse<{ mapping: string }>> => {
  return axios
    .patch(
      `merchant_payout_controls/${payoutControlId}/mappings`,
      { mapping: mappingId },
      {
        headers: {
          'content-type': 'application/merge-patch+json',
        },
      },
    )
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const merchantPayoutControlsCustomersUpdate = async (
  payoutControlId: number,
  customers: string[],
): Promise<AxiosResponse<{ customers: string[] }>> => {
  return axios
    .patch(
      `merchant_payout_controls/${payoutControlId}/customers`,
      { customers },
      {
        headers: {
          'content-type': 'application/merge-patch+json',
        },
      },
    )
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const merchantPayoutControlsCustomersCreate = async (
  merchantPayoutControl: number,
  merchant?: string,
  customerId?: string,
): Promise<AxiosResponse<{ customerId: string }>> => {
  return axios
    .post(
      'customers',
      {
        isTest: true,
        customerId,
        merchant,
        merchantPayoutControl: `admin/merchant_payout_controls/${merchantPayoutControl}`,
      },
      {
        headers: {
          'content-type': 'application/ld+json',
        },
      },
    )
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const merchantPayoutControlsCustomersDelete = async (
  payoutControlId: number,
  customerId?: number,
): Promise<AxiosResponse<{ customers: string[] }>> => {
  return axios
    .delete(`merchant_payout_controls/${payoutControlId}/customers/${customerId}`)
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const generateNotificationKeyRequest = async (): Promise<AxiosResponse> => {
  return axios
    .get('/generate/merchant_key')
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const createMerchantAccesses = async (
  merchant: number | undefined,
  users: number[],
): Promise<AxiosResponse<MerchantAccess>> => {
  return axios
    .post('merchant_accesses/bulk/create', {
      merchant,
      users,
    })
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const deleteMerchantAccesses = async (
  merchant: number | undefined,
  users: number[],
): Promise<AxiosResponse> => {
  return axios
    .delete('merchant_accesses/bulk/delete', {
      data: {
        merchant,
        users,
      },
    })
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

type ArchiveMerchantResponse = { id: number; archive: boolean };

export const archiveMerchant = async (
  id: string,
  archive: boolean,
): Promise<AxiosResponse<ArchiveMerchantResponse>> =>
  axios
    .patch(
      `/${Resources.Merchants}/${getId(id)}/archive`,
      { archive: archive },
      {
        headers: {
          'content-type': 'application/merge-patch+json',
        },
      },
    )
    .then((response: AxiosResponse) => response.data)
    .catch((error: AxiosError) => Promise.reject(error.response));

export const sendTestMerchantNotifications = async (
  notificationId: string,
): Promise<AxiosResponse<MerchantNotification>> => {
  return axios
    .get(`merchant_notification_settings/${notificationId}/test`, {
      headers: {
        Accept: '*/*',
      },
    })
    .then((response: AxiosResponse) => {
      return response.data;
    })
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export type MerchantNotificationsFilters = {
  page?: number;
  itemsPerPage?: number;
  'merchant.id'?: string;
};

export const getMerchantNotifications = async (
  filters?: MerchantNotificationsFilters,
): Promise<GetMerchantNotificationsResponse> => {
  const params = qs.stringify(
    {
      ...filters,
    },
    { arrayFormat: 'indices', encode: true },
  );
  return axios
    .get(`merchant_notification_settings${params ? '?' + params : ''}`, {
      headers: {
        Accept: '*/*',
      },
    })
    .then((response: AxiosResponse) => {
      return {
        merchantNotifications: response.data['hydra:member'],
        totalItems: response.data['hydra:totalItems'],
      };
    })
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const getNotificationTransports = async (): Promise<MerchantNotificationTransport[]> => {
  return axios
    .get('notification_transports', {
      headers: {
        Accept: '*/*',
      },
    })
    .then((response: AxiosResponse) => {
      return response.data['hydra:member'];
    })
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export type MerchantNotificationsEventNamesType = {
  '@id': string;
  description: string;
  label: string;
  name: string;
  value: string;
};

export const getMerchantNotificationsEventNames = async (): Promise<
  MerchantNotificationsEventNamesType[]
> => {
  return axios
    .get('merchant_notification_event_names', {
      headers: {
        Accept: '*/*',
      },
    })
    .then((response: AxiosResponse) => {
      return response.data['hydra:member'];
    })
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const getMerchantAccountCredentials = async (id: string): Promise<Credentials> => {
  return axios
    .get(`merchant_accounts/${id}/credentials`)
    .then((response: AxiosResponse<{ credentials: Credentials }>) => response.data.credentials)
    .catch((error: AxiosError) => Promise.reject(error.response));
};

export const getMerchantCredentialsJson = async (
  id: string,
): Promise<MerchantCredentials['credentials']> => {
  return axios
    .get(`merchant_credentials/${id}/show`)
    .then(
      (response: AxiosResponse<{ credentials: MerchantCredentials['credentials'] }>) =>
        response.data.credentials,
    )
    .catch((error: AxiosError) => Promise.reject(error.response));
};
